import {getAssetUrl} from '@Utils/s3.util';
import {showActionablePopup} from '@Components/actionable-popup/actionable-popup.library';

export enum ReviewDialog {
  POST_DOWNLOAD_EDITOR = 'post_download_editor',
  POST_PUBLISH_SOCIAL = 'post_publish_social',
  POST_PUBLISH_EMAIL = 'post_publish_email',
  POST_PUBLISH_EMAIL_BANNER = 'post_publish_email_banner',
  MY_STUFF_BANNER = 'my_stuff_banner',
  POST_EVENT_PUBLISH = 'post_event_publish',
  AI_CHAT_POPUP = 'ai_chat_popup',
  FOOTER_LINK = 'footer_link',
}

export enum ReviewRequestInteractionResult {
  BOUNCE = 0,
  SUCCESS = 1,
}

export interface ReviewRequestConfig {
  reviewDirectory: string;
  landingPageUrl: string;
  dialogImageUrl?: string;
}

interface CanShowReviewRequestResponse {
  showReviewRequest: boolean;
  reviewDirectory?: string;
  landingPageUrl?: string;
}

const CAN_SHOW_REVIEW_REQUEST_PATH = 'user/canShowReviewRequest';
const RECORD_REVIEW_REQUEST_SHOWN_PATH = 'user/recordReviewRequestShown';
const RECORD_REVIEW_REQUEST_INTERACTION_PATH = 'user/recordReviewRequestInteraction';
const NOTIFY_CONTENT_PLANNER_ACTIVITY_PATH = 'planner/sendReviewEmailForContentPlannerActivity';
const PRIMARY_REVIEW_REQUEST_IMAGE_NAME = 'review-dialog-image-1.png?ts=1';
const SECONDARY_REVIEW_REQUEST_IMAGE_NAME = 'review-dialog-image-2.png';

export const canShowReviewRequest = async (dialog: ReviewDialog): Promise<ReviewRequestConfig | null> => {
  try {
    const response = (await window.PMW.writeLocal(CAN_SHOW_REVIEW_REQUEST_PATH, {dialog})) as CanShowReviewRequestResponse;
    if (!response.showReviewRequest || !response.reviewDirectory || !response.landingPageUrl) {
      return null;
    }

    let dialogImageUrl = PRIMARY_REVIEW_REQUEST_IMAGE_NAME;
    if (dialog === ReviewDialog.POST_DOWNLOAD_EDITOR) {
      dialogImageUrl = SECONDARY_REVIEW_REQUEST_IMAGE_NAME;
    }

    return {
      reviewDirectory: response.reviewDirectory,
      landingPageUrl: response.landingPageUrl,
      dialogImageUrl,
    };
  } catch {
    return null;
  }
};

export const recordReviewRequestShown = async (dialog: ReviewDialog, reviewDirectory: string): Promise<void> => {
  try {
    await window.PMW.writeLocal(RECORD_REVIEW_REQUEST_SHOWN_PATH, {dialog, reviewDirectory});
  } catch {
    // intentionally ignored — impression recording is best-effort
  }
};

export const recordReviewRequestInteraction = async (dialog: ReviewDialog, reviewDirectory: string, result: ReviewRequestInteractionResult): Promise<void> => {
  try {
    await window.PMW.writeLocal(RECORD_REVIEW_REQUEST_INTERACTION_PATH, {dialog, reviewDirectory, result});
  } catch {
    // intentionally ignored — interaction recording is best-effort
  }
};

export const notifyContentPlannerActivityForReviewEmail = async (): Promise<void> => {
  try {
    await window.PMW.writeLocal(NOTIFY_CONTENT_PLANNER_ACTIVITY_PATH, {});
  } catch {
    // intentionally ignored
  }
};

export const showReviewPopupForConfig = (dialog: ReviewDialog, config: ReviewRequestConfig): void => {
  void recordReviewRequestShown(dialog, config.reviewDirectory);
  showActionablePopup({
    body: window.i18next.t('pmwjs_review_request_description'),
    image: {url: getAssetUrl(config.dialogImageUrl ?? PRIMARY_REVIEW_REQUEST_IMAGE_NAME), altText: ''},
    ctaButton: {
      text: window.i18next.t('pmwjs_review_request_cta'),
      onClick: (): void => {
        window.open(config.landingPageUrl, '_blank', 'noopener');
        void recordReviewRequestInteraction(dialog, config.reviewDirectory, ReviewRequestInteractionResult.SUCCESS);
      },
    },
    onClose: (): void => {
      void recordReviewRequestInteraction(dialog, config.reviewDirectory, ReviewRequestInteractionResult.BOUNCE);
    },
  });
};

export const showReviewToast = async (dialog: ReviewDialog): Promise<void> => {
  const config = await canShowReviewRequest(dialog);

  if (config === null) {
    return;
  }
  showReviewPopupForConfig(dialog, config);
};
